import React, { useState } from "react";
import { Box, TextField, Button, Typography, Container, CircularProgress } from "@mui/material";
import logoImage from "../../assets/images/logo.png";
import callApi from "../../services/callApi";

// ─── Estilos compartidos (idénticos al flujo de Registro) ───────────────────

const cardSx = {
  width: "100%",
  p: { xs: "20px", md: "28px" },
  borderRadius: { xs: "16px", md: "20px" },
  background: "#ffffff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.03), 0 8px 16px rgba(0,0,0,0.03), 0 12px 24px rgba(0,0,0,0.05)",
  border: "1px solid #eaeaea",
  boxSizing: "border-box",
};

const inputSx = {
  mb: 1,
  "& .MuiInputBase-root": {
    height: { xs: "44px", md: "46px" },
    backgroundColor: "#f7f9fc",
    fontFamily: "Quicksand-Regular",
    borderRadius: "10px",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    "& fieldset": { borderColor: "#e4e8ef" },
    "&:hover fieldset": { borderColor: "#a8b4c8" },
    "&.Mui-focused fieldset": { borderColor: "#1877f2", borderWidth: "1.5px" },
  },
  "& .MuiInputBase-input": { padding: "10px 14px", fontSize: { xs: "14px", md: "15px" } },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, 11px) scale(1)",
    fontFamily: "Quicksand-Regular",
    fontSize: { xs: "14px", md: "15px" },
  },
  "& .MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  "& .MuiInputLabel-asterisk": { display: "none" },
};

const btnSx = {
  mt: 2,
  height: { xs: "46px", md: "48px" },
  borderRadius: "10px",
  bgcolor: "#1877f2",
  fontFamily: "Quicksand-Bold",
  fontSize: { xs: "14px", md: "15px" },
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(24, 119, 242, 0.28)",
  transition: "all 0.2s ease",
  "&:hover": { bgcolor: "#166fe5", boxShadow: "0 6px 16px rgba(24, 119, 242, 0.38)" },
  "&.Mui-disabled": { bgcolor: "rgba(24, 119, 242, 0.35)", color: "white", boxShadow: "none" },
};

// ─── Derivar los 3 indicadores desde el mensaje API ─────────────────────────
// Cada indicador puede ser: true (sí), false (no), null (no aplica/desconocido)

const derivarIndicadores = (mensaje) => {
  switch (mensaje) {
    case "rut no valido":
      return { rutValido: false, registrado: null, suscripcion: null };
    case "profesional no registrado en clinifica":
      return { rutValido: true, registrado: false, suscripcion: null };
    case "profesional sin suscripcion de pago en clinifica":
      return { rutValido: true, registrado: true, suscripcion: false };
    case "profesional con suscripcion de pago en clinifica":
      return { rutValido: true, registrado: true, suscripcion: true };
    default:
      return null;
  }
};

// ─── Utilidades RUT ──────────────────────────────────────────────────────────

const formatRut = (value) => {
  let clean = value.replace(/[^0-9kK]+/g, "").toUpperCase();
  if (clean.length > 1) {
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    clean = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "-" + dv;
  }
  return clean;
};

const isRutValid = (r) => r.replace(/[^0-9kK]/g, "").length >= 3;

// ─── Sub-componente: una fila indicadora ─────────────────────────────────────

const COLORES = {
  si: { dot: "#16a34a", bg: "#f0fdf4", text: "#16a34a", label: "Sí"  },
  no: { dot: "#dc2626", bg: "#fef2f2", text: "#dc2626", label: "No"  },
  na: { dot: "#d1d5db", bg: "#f9fafb", text: "#9ca3af", label: "N/A" },
};

// Ícono SVG minimalista según estado (sin emojis)
const IconoEstado = ({ valor }) => {
  if (valor === true) {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <polyline points="2,7 5.5,10.5 12,3.5" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (valor === false) {
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="3" y1="3" x2="11" y2="11" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="11" y1="3" x2="3" y2="11" stroke="#dc2626" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="3" y1="7" x2="11" y2="7" stroke="#d1d5db" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

const IndicadorFila = ({ titulo, valor }) => {
  const c = valor === true ? COLORES.si : valor === false ? COLORES.no : COLORES.na;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "16px",
        py: "13px",
        borderRadius: "10px",
        border: "1px solid #eaeaea",
        background: "#fafafa",
      }}
    >
      {/* Texto */}
      <Typography
        sx={{
          fontFamily: "Quicksand-Regular",
          fontSize: { xs: "14px", md: "15px" },
          color: "#374151",
        }}
      >
        {titulo}
      </Typography>

      {/* Badge con ícono SVG */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          px: "10px",
          py: "5px",
          borderRadius: "20px",
          background: c.bg,
          minWidth: "52px",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <IconoEstado valor={valor} />
        <Typography
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "12px", md: "13px" },
            color: c.text,
            lineHeight: 1,
          }}
        >
          {c.label}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────

const VerificadorAdipa = () => {
  const [rut, setRut] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [errorRed, setErrorRed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResultado(null);
    setErrorRed(false);

    try {
      const data = await callApi(`/agenda/suscripcionEstadoPorRut?rut=${encodeURIComponent(rut)}`, "get");
      setResultado(data?.message || null);
    } catch {
      setErrorRed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNuevoRut = () => {
    setRut("");
    setResultado(null);
    setErrorRed(false);
  };

  const indicadores = resultado ? derivarIndicadores(resultado) : null;

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#ffffff",
        py: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth="xs" sx={{ px: { xs: 2, md: 2 } }}>
        <Box sx={cardSx}>

          {/* Logo */}
          <Box sx={{ mb: { xs: 2, md: 2.5 }, display: "flex", justifyContent: "center" }}>
            <img src={logoImage} alt="Clinifica" style={{ height: "34px" }} />
          </Box>

          {/* Título */}
          <Typography
            sx={{
              fontFamily: "Poppins-SemiBold",
              fontSize: { xs: "18px", md: "20px" },
              color: "#1c1c1c",
              mb: "6px",
            }}
          >
            Verificar suscripción
          </Typography>
          <Typography
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "15px" },
              color: "#6b7280",
              mb: { xs: "20px", md: "24px" },
              lineHeight: 1.5,
            }}
          >
            Ingresa el RUT del profesional para verificar su estado en Clinifica.
          </Typography>

          {/* Formulario */}
          {!resultado && (
            <Box component="form" onSubmit={handleSubmit} id="form-verificador-adipa">
              <TextField
                margin="normal"
                required
                fullWidth
                id="rut-adipa"
                label="RUT"
                name="rut"
                autoComplete="off"
                autoFocus
                value={rut}
                onChange={(e) => setRut(formatRut(e.target.value))}
                placeholder="XX.XXX.XXX-X"
                sx={inputSx}
                disabled={isLoading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                id="btn-verificar-adipa"
                disabled={!isRutValid(rut) || isLoading}
                sx={btnSx}
              >
                {isLoading ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  "Verificar"
                )}
              </Button>

              {errorRed && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: { xs: "12px", md: "13px" },
                    fontFamily: "Quicksand-Regular",
                    textAlign: "center",
                    mt: "12px",
                    p: "12px",
                    bgcolor: "rgba(211, 47, 47, 0.07)",
                    borderRadius: "10px",
                    lineHeight: 1.4,
                  }}
                >
                  Error de conexión. Intenta nuevamente.
                </Typography>
              )}
            </Box>
          )}

          {/* Resultado: 3 cards separadas */}
          {resultado && indicadores && (
            <>
              {/* RUT consultado */}
              <Typography
                sx={{
                  fontFamily: "Quicksand-Regular",
                  fontSize: { xs: "12px", md: "13px" },
                  color: "#9ca3af",
                  mb: "12px",
                  textAlign: "center",
                }}
              >
                RUT consultado: <strong style={{ color: "#6b7280" }}>{rut}</strong>
              </Typography>

              {/* 3 cards independientes */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mb: "4px" }}>
                <IndicadorFila titulo="RUT válido"              valor={indicadores.rutValido}   />
                <IndicadorFila titulo="Registrado en Clinifica" valor={indicadores.registrado}  />
                <IndicadorFila titulo="Suscripción de pago"     valor={indicadores.suscripcion} />
              </Box>
            </>
          )}

          {/* Botón "Verificar otro RUT" */}
          {resultado && (
            <Button
              fullWidth
              variant="text"
              onClick={handleNuevoRut}
              id="btn-nuevo-rut-adipa"
              sx={{
                mt: "14px",
                color: "#1877f2",
                textTransform: "none",
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "14px", md: "15px" },
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              Verificar otro RUT
            </Button>
          )}

        </Box>
      </Container>
    </Box>
  );
};

export default VerificadorAdipa;
